import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Box, MenuItem, Select, FormControl, InputLabel, Grid, Alert, IconButton
} from '@mui/material';
import { Add, Remove, ArrowBack } from '@mui/icons-material';
import api from '../../services/api';
import BSDateInput from '../common/BSDateInput';

const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];
const unitOptions = ['Pcs', 'Pair', 'Set'];

const ShopDirectOrder = () => {
  const [karigar, setKarigar] = useState('');
  const [karigars, setKarigars] = useState([]);
  const [salesman, setSalesman] = useState('');
  const [order, setOrder] = useState({ billNumber: '', orderDate: '', deliveryDate: '', requiredDate: '', isUrgent: false });
  const [items, setItems] = useState([
    { itemName: '', karat: '', quantity: '', unit: '', itemWeight: '' }
  ]);
  const [itemOptions, setItemOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchKarigars = async () => {
      try {
        const res = await api.get('/api/karigar');
        setKarigars(res.data);
      } catch {}
    };
    const fetchItems = async () => {
      try {
        const res = await api.get('/api/items');
        setItemOptions(res.data.map(item => item.name));
      } catch {}
    };
    fetchKarigars();
    fetchItems();
  }, []);

  const handleKarigarChange = e => setKarigar(e.target.value);
  const handleSalesmanChange = e => setSalesman(e.target.value);
  const handleOrderChange = e => {
    const { name, value, type, checked } = e.target;
    setOrder(o => ({ ...o, [name]: type === 'checkbox' ? checked : value }));
  };
  const handleItemChange = (idx, e) => {
    const { name, value } = e.target;
    setItems(items => items.map((item, i) => i === idx ? { ...item, [name]: value } : item));
  };
  const addItem = () => setItems(items => [...items, { itemName: '', karat: '', quantity: '', unit: '', itemWeight: '' }]);
  const removeItem = idx => setItems(items => items.length > 1 ? items.filter((_, i) => i !== idx) : items);

  const validate = () => {
    if (!karigar || !karigar.trim()) return 'Karigar is required.';
    if (!salesman || !salesman.trim()) return 'Salesman is required.';
    if (!order.billNumber || !order.billNumber.trim() || !order.orderDate || !order.deliveryDate || !order.requiredDate) return 'All order fields are required.';
    for (const item of items) {
      if (!item.itemName || !item.itemName.trim() || !item.karat || !item.karat.trim() || !item.quantity || !item.unit || !item.unit.trim() || !item.itemWeight) return 'All item fields are required.';
    }
    return '';
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const err = validate();
    if (err) { setError(err); return; }
    setLoading(true);
    try {
      await api.post('/api/shop/direct-orders', {
        billNumber: order.billNumber,
        orderDate: order.orderDate,
        deliveryDate: order.deliveryDate,
        requiredDate: order.requiredDate,
        status: 'Pending',
        agentName: salesman,
        karigarAbbreviation: karigar,
        items: items.map(item => ({
          ...item,
          quantity: Number(item.quantity),
          itemWeight: Number(item.itemWeight),
          total: Number(item.itemWeight),
          netAmount: 0,
          totalAmount: 0,
          gsb: 0,
          wastage: 0,
          rate: 0,
          makingCharge: 0,
          stoneAmount: 0,
          despatchStatus: true,
          workIssued: false,
          karigarAbbreviation: karigar
        })),
        totalAmount: 0,
        isUrgent: false,
        isActive: true,
        createdBy: 'shop'
      });
      setSuccess('Order created successfully!');
      setKarigar('');
      setSalesman('');
      setOrder({ billNumber: '', orderDate: '', deliveryDate: '', requiredDate: '', isUrgent: false });
      setItems([{ itemName: '', karat: '', quantity: '', unit: '', itemWeight: '' }]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create order');
    }
    setLoading(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" color="primary">Back</Button>
        </Box>
        <Typography variant="h4" gutterBottom color="primary">
          Shop Direct Order Entry
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" sx={{ mt: 2 }}>Order Details</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Karigar Name</InputLabel>
                <Select value={karigar} label="Karigar Name" onChange={handleKarigarChange}>
                  {karigars.map(k => (
                    <MenuItem key={k.abbreviation} value={k.abbreviation}>
                      {k.abbreviation} - {k.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Salesman" name="salesman" value={salesman} onChange={handleSalesmanChange} required fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Bill Number" name="billNumber" value={order.billNumber} onChange={handleOrderChange} required fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={2}>
              <BSDateInput
                label="Order Date"
                value={order.orderDate}
                onChange={(date) => setOrder(o => ({ ...o, orderDate: date }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <BSDateInput
                label="Delivery Date"
                value={order.deliveryDate}
                onChange={(date) => setOrder(o => ({ ...o, deliveryDate: date }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <BSDateInput
                label="Required Date"
                value={order.requiredDate}
                onChange={(date) => setOrder(o => ({ ...o, requiredDate: date }))}
                required
              />
            </Grid>
          </Grid>
          <Typography variant="h6" sx={{ mt: 2 }}>Order Items</Typography>
          {items.map((item, idx) => (
            <Grid container spacing={2} key={idx} alignItems="center" sx={{ mb: 1 }}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Item Name</InputLabel>
                  <Select name="itemName" value={item.itemName} label="Item Name" onChange={e => handleItemChange(idx, e)}>
                    {itemOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Karat</InputLabel>
                  <Select name="karat" value={item.karat} label="Karat" onChange={e => handleItemChange(idx, e)}>
                    {karatOptions.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField label="Quantity" name="quantity" type="number" value={item.quantity} onChange={e => handleItemChange(idx, e)} required fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Unit</InputLabel>
                  <Select name="unit" value={item.unit} label="Unit" onChange={e => handleItemChange(idx, e)}>
                    {unitOptions.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField label="Item Weight" name="itemWeight" type="number" value={item.itemWeight} onChange={e => handleItemChange(idx, e)} required fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={1}>
                <IconButton onClick={() => removeItem(idx)} disabled={items.length === 1}><Remove /></IconButton>
                <IconButton onClick={addItem}><Add /></IconButton>
              </Grid>
            </Grid>
          ))}
          <Box sx={{ mt: 3 }}>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Order'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default ShopDirectOrder; 